import { ColumnDef } from '@tanstack/react-table'
import type { MyAssignment } from './api'
import type { RecoverRequest } from '@/features/recover-requests/api'
import { Button } from '@/components/ui/button'
import { FileJson, List, Map, UserCheck, UserMinus, FileStack, QrCode, CheckCircle2 } from 'lucide-react'
import { AnalyticsStatusIndicator } from '@/lib/utils/analytics-status'
import { buildIconPath } from '@/lib/imagePaths'
import { EntityIcon } from '@/components/ui/entity-icon'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { formatDateForLocale } from '@/lib/dateUtils'
import { hasCaveats, waitGoTitle, WAITGO_CAVEAT_BORDER } from '@/features/recover-requests/components/WaitGoState'

// Helper function to normalize MySQL DATETIME strings to UTC ISO format
// MySQL returns dates as "2026-01-23 09:57:00" (no timezone), but they're stored as UTC
const normalizeUtcDate = (datetime: string | Date | null | undefined): string | null => {
    if (!datetime) return null
    if (datetime instanceof Date) {
        return datetime.toISOString()
    }
    if (typeof datetime === 'string') {
        // Convert MySQL format "2026-01-23 09:57:00" to ISO "2026-01-23T09:57:00Z"
        let dateStr = datetime.includes('T') ? datetime : datetime.replace(' ', 'T')
        
        // If no timezone info, treat as UTC (append 'Z')
        if (!dateStr.includes('Z') && !dateStr.match(/[+-]\d{2}:\d{2}$/)) {
            dateStr = dateStr.endsWith('Z') ? dateStr : dateStr + 'Z'
        }
        
        return dateStr
    }
    return null
}

// Helper function to format date/time with locale support
// Uses existing formatDateForLocale utility which handles UTC to local conversion
const formatDate = (datetime: string | Date | null | undefined, locale: string = 'en-GB') => {
    const normalizedDate = normalizeUtcDate(datetime)
    if (!normalizedDate) return '-'
    
    try {
        return formatDateForLocale(normalizedDate, locale, {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            hour12: false
        })
    } catch (error) {
        console.warn('Failed to format date:', error)
        return '-'
    }
}

const getStatusCircle = (analytics: number | null) => (
    <AnalyticsStatusIndicator status={analytics} size="h-4 w-4" />
)

export function createRecoverRequestColumns(
    t: (key: string) => string,
    locale: string = 'en-GB',
    onShowMetadata?: (title: string, content: string | null) => void,
    onShowRunbook?: (objectID: string, scenario: number | null, forecastStartTime: string | Date | null) => void,
    onShowFloorPlan?: (objectID: string) => void,
    onAssignEngineer?: (objectID: string, recoverRequestId: number | null, waitGo: string | null, analytics: number | null, responsibleTeamId: number | null, locationId: number | null) => void,
    onShowObjectDetails?: (objectID: string, requestID: string) => void,
    onShowQRCode?: (objectID: string) => void,
    onUnassign?: (objectID: string, objectDescription: string | null, recoverRequestId: number | null) => void,
    onStartRecovery?: (objectID: string, objectDescription: string | null, recoverRequestId: number | null, scenario: number | null, forecastStartTime: string | Date | null, requestID: string | null) => void,
    onMarkComplete?: (objectID: string, objectDescription: string | null, recoverRequestId: number | null) => void
): ColumnDef<MyAssignment | RecoverRequest>[] {
    return [
        {
            accessorKey: 'objectID',
            header: t('recoverRequests.objectID'),
            size: 320,
            minSize: 280,
            cell: ({ row }) => {
                const objectID = row.getValue('objectID') as string | null
                const description = row.original.objectDescription
                const requestID = row.original.requestID
                const requestObjectCount = row.original.requestObjectCount || 0
                const hoverText = [objectID, description].filter(Boolean).join(' — ')
                return (
                    <div className="flex flex-col min-w-0" title={hoverText || undefined}>
                        <span className="text-sm font-medium truncate">{objectID || '-'}</span>
                        {description && (
                            <span className="text-xs text-muted-foreground truncate">{description}</span>
                        )}
                    </div>
                )
            },
        },
        {
            id: 'objectIcon',
            header: '',
            size: 48,
            minSize: 48,
            maxSize: 48,
            meta: { compact: true },
            cell: ({ row }) => {
                const iconFilename = row.original.iconFilename
                if (!iconFilename) return null
                return (
                    <div className="flex items-center justify-center">
                        <EntityIcon src={buildIconPath(iconFilename)} alt="" className="h-8 w-8" />
                    </div>
                )
            },
        },
        {
            id: 'qrCode',
            header: '',
            size: 32,
            minSize: 28,
            maxSize: 36,
            cell: ({ row }) => {
                const objectID = row.getValue('objectID') as string | null
                if (!objectID || !onShowQRCode) return <div className="flex items-center justify-center px-0">-</div>
                return (
                    <div className="flex items-center justify-center px-0">
                        <Button
                            variant="ghost"
                            size="sm"
                            className="h-6 w-6 p-0"
                            onClick={(e) => {
                                e.stopPropagation()
                                onShowQRCode(objectID)
                            }}
                            title={t('objects.showQRCode')}
                        >
                            <QrCode className="h-3.5 w-3.5" />
                        </Button>
                    </div>
                )
            },
        },
        {
            id: 'subObjects',
            header: '',
            size: 32,
            minSize: 28,
            maxSize: 36,
            cell: ({ row }) => {
                const objectID = row.getValue('objectID') as string | null
                const requestID = row.original.requestID
                const requestObjectCount = row.original.requestObjectCount || 0
                if (!objectID || !requestID || !onShowObjectDetails || requestObjectCount <= 1) {
                    return <div className="flex items-center justify-center px-0">-</div>
                }
                return (
                    <div className="flex items-center justify-center px-0">
                        <Button
                            variant="ghost"
                            size="sm"
                            className="h-6 w-6 p-0"
                            onClick={(e) => {
                                e.stopPropagation()
                                onShowObjectDetails(objectID, requestID)
                            }}
                            title={t('recoverRequests.viewObjectDetails')}
                        >
                            <FileStack className="h-3.5 w-3.5" />
                        </Button>
                    </div>
                )
            },
        },
        {
            accessorKey: 'versionMetadata',
            header: '',
            size: 32,
            minSize: 28,
            maxSize: 36,
            cell: ({ row }) => {
                const metadata = row.getValue('versionMetadata') as string | null
                if (!metadata) return <div className="flex items-center justify-center px-0"><span className="text-muted-foreground">-</span></div>
                return (
                    <div className="flex items-center justify-center px-0">
                        <Button
                            variant="ghost"
                            size="sm"
                            className="h-6 w-6 p-0"
                            onClick={() => onShowMetadata?.(t('recoverRequests.metadata'), metadata)}
                        >
                            <FileJson className="h-3.5 w-3.5" />
                        </Button>
                    </div>
                )
            },
        },
        {
            accessorKey: 'versionAnalytics',
            header: t('recoverRequests.analytics'),
            size: 80,
            minSize: 70,
            maxSize: 90,
            cell: ({ row }) => {
                const analytics = row.getValue('versionAnalytics') as number | null
                return (
                    <div className="flex items-center justify-center">
                        {getStatusCircle(analytics)}
                    </div>
                )
            },
        },
        {
            accessorKey: 'location',
            header: t('recoverRequests.location'),
            size: 140,
            minSize: 100,
            maxSize: 180,
            cell: ({ row }) => {
                const location = row.getValue('location') as string | null
                return <span className="text-sm">{location || '-'}</span>
            },
        },
        {
            accessorKey: 'teamName',
            header: t('recoverRequests.teamName'),
            size: 130,
            minSize: 100,
            maxSize: 180,
            cell: ({ row }) => {
                const teamName = row.getValue('teamName') as string | null
                return <span className="text-sm">{teamName || '-'}</span>
            },
        },
        {
            accessorKey: 'forecastStartTime',
            header: t('recoverRequests.forecastStartTime'),
            size: 140,
            minSize: 120,
            maxSize: 160,
            cell: ({ row }) => {
                const forecastStartTime = row.getValue('forecastStartTime') as string | Date | null
                return (
                    <span className="text-sm whitespace-nowrap">
                        {forecastStartTime ? formatDate(forecastStartTime, locale) : '-'}
                    </span>
                )
            },
        },
        {
            accessorKey: 'objectReady',
            header: t('recoverRequests.objectReady'),
            size: 110,
            minSize: 90,
            maxSize: 140,
            cell: ({ row }) => {
                const objectReady = row.getValue('objectReady') as number | null
                // Debug logging
                if (process.env.NODE_ENV === 'development') {
                    console.log('🔍 DEBUG ObjectReady column:', {
                        objectReady,
                        objectReadyType: typeof objectReady,
                        rowData: row.original
                    })
                }
                if (objectReady === null || objectReady === undefined) {
                    return <span className="text-sm text-muted-foreground">-</span>
                }
                const isReady = objectReady === 1
                return (
                    <span className={`text-sm ${isReady ? 'text-green-600' : 'text-amber-600'}`}>
                        {isReady ? t('recoverRequests.objectReadyStatus') : t('recoverRequests.copyInProgress')}
                    </span>
                )
            },
        },
        {
            accessorKey: 'waitGo',
            header: () => <div>{t('recoverRequests.status')}</div>,
            size: 180,
            minSize: 160,
            maxSize: 200,
            cell: ({ row }) => {
                const waitGo = row.getValue('waitGo') as string | null
                const objectID = row.original.objectID
                const scenario = row.original.scenario
                const forecastStartTime = row.original.forecastStartTime
                
                if (!waitGo) return <span className="text-muted-foreground">-</span>
                const waitGoUpper = waitGo.toUpperCase()
                const isGo = waitGoUpper === 'GO'
                // Start (recovery.execute) or assign (recover-requests.update): a handler is passed
                // only to a role that holds the permission, so without one the square is status only.
                const canAct = !!(onStartRecovery || onAssignEngineer)
                return (
                    <div className="flex items-center justify-center gap-1 h-full">
                        <TooltipProvider>
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        className={`w-12 h-12 aspect-square rounded flex items-center justify-center text-sm font-medium flex-shrink-0 p-0 cursor-pointer ${
                                            isGo
                                                ? 'bg-[rgb(151,200,100)] text-white hover:bg-[rgb(151,200,100)] hover:text-white animate-assign-start-pulse'
                                                : 'bg-[rgb(232,96,55)] text-white hover:bg-[rgb(232,96,55)] hover:text-white'
                                        }`}
                                        style={hasCaveats(row.original) ? { border: `2px solid ${WAITGO_CAVEAT_BORDER}` } : undefined}
                                        title={waitGoTitle(row.original)}
                                        disabled={!canAct}
                                        onClick={(e) => {
                                            e.stopPropagation()
                                            if (objectID && onStartRecovery) {
                                                const recoverRequestId = row.original.id
                                                onStartRecovery(objectID, row.original.objectDescription ?? null, recoverRequestId, row.original.scenario, row.original.forecastStartTime ?? null, row.original.requestID ?? null)
                                            } else if (objectID && onAssignEngineer) {
                                                const recoverRequestId = row.original.id
                                                const analytics = row.original.versionAnalytics
                                                const responsibleTeamId = row.original.responsibleTeamId
                                                onAssignEngineer(objectID, recoverRequestId, waitGo, analytics, responsibleTeamId, row.original.locationId || null)
                                            }
                                        }}
                                    >
                                        {isGo ? t('myAssignments.start') : waitGoUpper}
                                    </Button>
                                </TooltipTrigger>
                                <TooltipContent>
                                    <p>{t('myAssignments.start')}</p>
                                </TooltipContent>
                            </Tooltip>
                        </TooltipProvider>
                        {objectID && (
                            <TooltipProvider>
                                <>
                                    <Tooltip>
                                        <TooltipTrigger asChild>
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                className="h-8 w-8 p-0 text-blue-600 hover:text-blue-700 flex-shrink-0"
                                                onClick={() => onShowRunbook?.(objectID, scenario, forecastStartTime)}
                                            >
                                                <List className="h-4 w-4" />
                                            </Button>
                                        </TooltipTrigger>
                                        <TooltipContent>
                                            <p>{t('recoverRequests.viewRunbook')}</p>
                                        </TooltipContent>
                                    </Tooltip>
                                    <Tooltip>
                                        <TooltipTrigger asChild>
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                className="h-8 w-8 p-0 text-blue-600 hover:text-blue-700 flex-shrink-0"
                                                onClick={() => onShowFloorPlan?.(objectID)}
                                            >
                                                <Map className="h-4 w-4" />
                                            </Button>
                                        </TooltipTrigger>
                                        <TooltipContent>
                                            <p>{t('recoverRequests.viewFloorPlan')}</p>
                                        </TooltipContent>
                                    </Tooltip>
                                    {canAct && (
                                        <Tooltip>
                                            <TooltipTrigger asChild>
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    className="h-8 w-8 p-0 text-blue-600 hover:text-blue-700 flex-shrink-0"
                                                    onClick={(e) => {
                                                        e.stopPropagation()
                                                        if (onStartRecovery) {
                                                            const recoverRequestId = row.original.id
                                                            onStartRecovery(objectID, row.original.objectDescription ?? null, recoverRequestId, row.original.scenario, row.original.forecastStartTime ?? null, row.original.requestID ?? null)
                                                        } else if (onAssignEngineer) {
                                                            const recoverRequestId = row.original.id
                                                            const analytics = row.original.versionAnalytics
                                                            const responsibleTeamId = row.original.responsibleTeamId
                                                            onAssignEngineer(objectID, recoverRequestId, waitGo, analytics, responsibleTeamId, row.original.locationId || null)
                                                        }
                                                    }}
                                                >
                                                    <UserCheck className="h-4 w-4" />
                                                </Button>
                                            </TooltipTrigger>
                                            <TooltipContent>
                                                <p>{t('myAssignments.start')}</p>
                                            </TooltipContent>
                                        </Tooltip>
                                    )}
                                    {onUnassign && (
                                        <Tooltip>
                                            <TooltipTrigger asChild>
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    className="h-8 w-8 p-0 text-muted-foreground hover:text-destructive flex-shrink-0"
                                                    onClick={(e) => {
                                                        e.stopPropagation()
                                                        e.preventDefault()
                                                        const recoverRequestId = row.original.id
                                                        if (objectID && recoverRequestId != null && onUnassign) {
                                                            onUnassign(objectID, row.original.objectDescription ?? null, recoverRequestId)
                                                        }
                                                    }}
                                                >
                                                    <UserMinus className="h-4 w-4" />
                                                </Button>
                                            </TooltipTrigger>
                                            <TooltipContent>
                                                <p>{t('myAssignments.unassign')}</p>
                                            </TooltipContent>
                                        </Tooltip>
                                    )}
                                    {onMarkComplete && (
                                        <Tooltip>
                                            <TooltipTrigger asChild>
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    className="h-8 w-8 p-0 text-green-600 hover:text-green-700 flex-shrink-0"
                                                    onClick={(e) => {
                                                        e.stopPropagation()
                                                        e.preventDefault()
                                                        const recoverRequestId = row.original.id
                                                        if (objectID && recoverRequestId != null) {
                                                            onMarkComplete(objectID, row.original.objectDescription ?? null, recoverRequestId)
                                                        }
                                                    }}
                                                >
                                                    <CheckCircle2 className="h-4 w-4" />
                                                </Button>
                                            </TooltipTrigger>
                                            <TooltipContent>
                                                <p>{t('myAssignments.markComplete')}</p>
                                            </TooltipContent>
                                        </Tooltip>
                                    )}
                                </>
                            </TooltipProvider>
                        )}
                    </div>
                )
            },
        },
    ]
}
